import { useState, useEffect, useMemo } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { MstVendorDetail, Vendor, VendorListRequest } from "../types/vendor";
import { getVendorListApi, updateVendorApi } from "../api/vendorApi";

/**
 * Custom hook to fetch and manage a paginated list of vendors.
 * This hook uses a placeholder for the actual API call. Replace the commented-out fetchClients function with the actual API call to fetch vendors.
 */
const useVendorList = () => {
  const [queryParams, setQueryParams] = useState<{
    vendor_cd_or_name?: string;
    status_flag?: number;
    send_pattern?: number;
  }>({ status_flag: 1 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [vendors, setVendors] = useState<Vendor[]>([]);

  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);

  /**
   * @param body VendorQuery
   */
  const getVendors = async (body?: VendorListRequest) => {
    try {
      const data = await getVendorListApi(body);
      if (body && body?.offset > 1) {
        setVendors((prev) => [...prev, ...data.list]);
      } else {
        setVendors(data.list);
      }
      setTotal(data.total);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  useEffect(() => {
    getVendors({ status_flag: 1, offset: 1, limit: DEFAULT_LIMIT_ITEMS });
  }, []);

  const handleSearch = async () => {
    await getVendors({
      ...queryParams,
      offset: 1,
      limit: DEFAULT_LIMIT_ITEMS,
    });
  };

  const onLoadMore = async () => {
    if (page + 1 > totalPage) return;
    setLoading(true);
    setPage((prevPage) => prevPage + 1);
    await getVendors({ ...queryParams, offset: page + 1, limit: DEFAULT_LIMIT_ITEMS });
  };

  const onUpdateVendor = async (vendor: MstVendorDetail) => {
    await updateVendorApi(vendor);
    await getVendors({ ...queryParams, offset: 1, limit: DEFAULT_LIMIT_ITEMS });
    setPage(1);
  }

  return {
    vendors,
    queryParams,
    total,
    totalPage,
    page,
    loading,
    handleSearch,
    onLoadMore,
    setQueryParams,
    onUpdateVendor,
  };
};

export default useVendorList;
